import { RestError } from '@azure/core-http';
import {
  KnownDeliveredIntoDtoType,
  KnownDeliveryDtoState,
  OpenLockerResponseDto
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { useMemo, useState } from 'react';
import { Delivery } from '../../../models/delivery.model';
import {
  useMutationMarkDeliveryCancel,
  useMutationMarkDeliveryCollected,
  useMutationMarkDeliveryRetrieved,
  useQueryCanIOpenDeliveryLocker
} from '../../deliveries-details.queries';
import { useMutationOpenLocker, useQueryListLockerDoors } from '../../../../lockers/lockers.queries';

export interface OpenLockerActionProps {
  delivery: Delivery;
  onAbort(): void;
  onSuccess(statusChanged: boolean): void;
}

interface OpenLockerActionState {
  error?: RestError;
  selectedDoorID: string;
}

interface IntentToOpen extends OpenLockerActionState {
  current: 'OPEN_REQUEST_INTENT_PENDING';
  loading: boolean;
}

interface OpenRequestFailed extends OpenLockerActionState {
  current: 'OPEN_REQUEST_FAILED';
  response?: OpenLockerResponseDto;
  loading: boolean;
}

interface OpenRequestSuccess extends OpenLockerActionState {
  current: 'OPEN_REQUEST_SUCCESS';
  loading: boolean;
  error?: RestError;
}

export type OpenLockerActionStates = IntentToOpen | OpenRequestFailed | OpenRequestSuccess;

export type ValidDeliveryStatusChangeOption = Extract<
  `${KnownDeliveryDtoState}`,
  'collected' | 'cancelled' | 'retrieved'
>;

export interface OpenLockerActionHandlers {
  onCancelIntent(): void;
  onSendRequest(): void;
  onChangeStatus(): void;
  onStatusInput(status: ValidDeliveryStatusChangeOption): void;
  onContinueToStatusChange(): void;
  onDone(): void;
}

const initialState: OpenLockerActionStates = {
  current: 'OPEN_REQUEST_INTENT_PENDING',
  loading: false,
  selectedDoorID: ''
};

export function useOpenLockerAction({ onAbort, onSuccess, delivery }: OpenLockerActionProps) {
  const [state, setState] = useState<OpenLockerActionStates>(initialState);
  const [newStatus, setNewStatus] = useState<ValidDeliveryStatusChangeOption | undefined>();

  // Get the Locker ID so we can fetch the list of Doors (if it is in fact a Locker)
  const lockerID = useMemo(() => {
    if (!delivery.deliveredInto || delivery.deliveredInto.type !== KnownDeliveredIntoDtoType.Locker) {
      return undefined;
    }
    return delivery.deliveredInto.id;
  }, [delivery.deliveredInto]);

  const doors = useQueryListLockerDoors(lockerID);

  // Even if the User has permission to open this locker, they still might not be able to.
  // For example, the locker might contain deliveries for another project.
  const canOpenLocker = useQueryCanIOpenDeliveryLocker(delivery.id);

  const isLoading = doors.isLoading || canOpenLocker.isLoading;

  const openLockerMutation = useMutationOpenLocker();
  const cancelDeliveryMutation = useMutationMarkDeliveryCancel();
  const markDeliveryCollectedMutation = useMutationMarkDeliveryCollected();
  const markDeliveryRetrievedMutation = useMutationMarkDeliveryRetrieved();

  // Prepare the Door select options
  const doorOptions = useMemo(() => {
    if (doors.isLoading) {
      return [];
    }
    // We only show door select options if there are multiple doors
    if (!doors.data || doors.data.length < 2) {
      return [];
    }
    return doors.data.map((door) => ({
      label: door.positionDescription ?? `Door with hinge on ${door.hingeSide.toLowerCase()} side.`,
      value: door.id
    }));
  }, [doors.data, doors.isLoading]);

  const multipleDoorsProps = useMemo(() => {
    // We only show door select options if there are multiple doors
    if (doorOptions.length < 2) {
      return undefined;
    }
    return {
      doorOptions,
      onSelectDoor(doorID: string) {
        setState((state) => ({ ...state, selectedDoorID: doorID }));
      }
    };
  }, [doorOptions]);

  const handlers: OpenLockerActionHandlers = {
    onCancelIntent() {
      switch (state.current) {
        case 'OPEN_REQUEST_INTENT_PENDING':
        case 'OPEN_REQUEST_FAILED':
          onAbort();
          break;
        default:
          return;
      }
    },
    onSendRequest() {
      if (lockerID === undefined) {
        return;
      }
      switch (state.current) {
        case 'OPEN_REQUEST_INTENT_PENDING':
          setState((state) => ({ ...state, current: 'OPEN_REQUEST_INTENT_PENDING', loading: true }));
          openLockerMutation.mutate(
            { lockerID, doorIDs: state.selectedDoorID ? [state.selectedDoorID] : undefined },
            {
              onSuccess(response: OpenLockerResponseDto) {
                if (response.status === 'success') {
                  setState((state) => ({ ...state, current: 'OPEN_REQUEST_SUCCESS', loading: false }));
                } else {
                  setState((state) => ({ ...state, current: 'OPEN_REQUEST_FAILED', loading: false, response }));
                }
              },
              onError(error: RestError) {
                setState((state) => ({ ...state, current: 'OPEN_REQUEST_FAILED', loading: false, error }));
              }
            }
          );
          break;
        case 'OPEN_REQUEST_FAILED':
          setState((state) => ({ ...state, current: 'OPEN_REQUEST_FAILED', loading: true }));
          openLockerMutation.mutate(
            { lockerID, doorIDs: state.selectedDoorID ? [state.selectedDoorID] : undefined },
            {
              onSuccess(response: OpenLockerResponseDto) {
                if (response.status === 'success') {
                  setState((state) => ({ ...state, current: 'OPEN_REQUEST_SUCCESS', loading: false }));
                } else {
                  setState((state) => ({ ...state, current: 'OPEN_REQUEST_FAILED', loading: false, response }));
                }
              },
              onError(error: RestError) {
                setState((state) => ({ ...state, current: 'OPEN_REQUEST_FAILED', loading: false, error }));
              }
            }
          );
          break;
        default:
          return;
      }
    },
    onStatusInput(newStatus) {
      switch (state.current) {
        case 'OPEN_REQUEST_SUCCESS':
          setNewStatus(newStatus);
          break;
        default:
          return;
      }
    },
    onChangeStatus() {
      switch (state.current) {
        case 'OPEN_REQUEST_SUCCESS':
          if (!newStatus) {
            return;
          }

          setState((state) => ({ ...state, current: 'OPEN_REQUEST_SUCCESS', loading: true }));
          const statusHasChanged = true;

          const mutationOptions = {
            onSuccess() {
              onSuccess(statusHasChanged);
            },
            onError(error: RestError) {
              setState((state) => ({ ...state, current: 'OPEN_REQUEST_SUCCESS', error, loading: false }));
            }
          };
          if (newStatus === 'cancelled') {
            cancelDeliveryMutation.mutate({ deliveryID: delivery.id }, mutationOptions);
          }
          if (newStatus === 'collected') {
            markDeliveryCollectedMutation.mutate({ deliveryID: delivery.id }, mutationOptions);
          }
          if (newStatus === 'retrieved') {
            markDeliveryRetrievedMutation.mutate({ deliveryID: delivery.id }, mutationOptions);
          }
          break;
        default:
          return;
      }
    },
    onContinueToStatusChange() {
      switch (state.current) {
        case 'OPEN_REQUEST_FAILED':
          setState((state) => ({ ...state, current: 'OPEN_REQUEST_SUCCESS', loading: false }));
      }
    },
    onDone() {
      switch (state.current) {
        case 'OPEN_REQUEST_SUCCESS':
          const statusHasChanged = false;
          onSuccess(statusHasChanged);
          break;
        default:
          return;
      }
    }
  };

  return { state, newStatus, handlers, multipleDoorsProps, isLoading, canOpenLocker: canOpenLocker.data } as const;
}
